import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Form,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  TextField,
} from '@customerio/pluma-components/react';
import { z } from '@customerio/hydra-validation';
import { useWatch, useZodForm } from '@/components/form';
import { PasswordStrengthMeter, usePasswordValidation } from '@/features/password-strength';
import { isEmptyString } from '@/utils/string';
import { useSignupFlow } from '../signup-flow';
import { SignupAnalyticsEvent, signupTypeProps } from '../utils/analytics';
import { buildRouteSequence } from '../utils/flow-sequence';
import * as styles from './password-step.css';

// Length, breach and strength are owned by `usePasswordValidation`, which gates
// submit; the schema only carries the cross-field rule.
const passwordStepSchema = z
  .object({
    password: z.string(),
    passwordConfirmation: z.string(),
  })
  .refine((values) => values.passwordConfirmation === values.password, {
    message: "Your passwords don't match.",
    path: ['passwordConfirmation'],
  });

const RECOMMENDATION =
  'We recommend your password contain at least one number, one special character, one capital letter, and be at least 10 characters long.';

export function PasswordStep() {
  const {
    formValues,
    patchFormValues,
    effectiveSignupType,
    isGoogleSignup,
    isStartup,
    goToNextStep,
    trackSignupEvent,
  } = useSignupFlow();

  const form = useZodForm({
    schema: passwordStepSchema,
    defaultValues: { password: formValues.password, passwordConfirmation: formValues.passwordConfirmation },
  });

  const password = useWatch({ control: form.control, name: 'password' });

  // zxcvbn only penalizes a password reusing the visitor's own identifiers when
  // it is told what they are.
  const userInputs = useMemo(
    () => [formValues.email, formValues.firstName, formValues.lastName].filter((value) => !isEmptyString(value)),
    [formValues.email, formValues.firstName, formValues.lastName],
  );
  const { score, warning, suggestions, error, isValid, isValidating } = usePasswordValidation(password, { userInputs });

  // Once the field has been visited with a value the feedback stays up, even if
  // the password is cleared again.
  const [passwordVisited, setPasswordVisited] = useState(!isEmptyString(formValues.password));
  const showStrengthFeedback = passwordVisited || password !== '';
  const [confirmationTouched, setConfirmationTouched] = useState(!isEmptyString(formValues.passwordConfirmation));

  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    trackSignupEvent(
      SignupAnalyticsEvent.PasswordViewed,
      signupTypeProps({ signupType: effectiveSignupType, isGoogleSignup }),
    );
  }, [trackSignupEvent, effectiveSignupType, isGoogleSignup]);

  // `isValid` is stale from the previous password while validation is in
  // flight, so submitting has to wait for it to settle.
  const canSubmit = confirmationTouched && form.formState.isValid && isValid && !isValidating;

  const passwordField = form.registerPluma('password');
  const confirmationFieldProps = form.registerPluma('passwordConfirmation');

  const onSubmit = form.handleSubmit(async () => {
    if (!canSubmit) {
      return; // guard against Enter-key bypass of the disabled button
    }
    trackSignupEvent(
      SignupAnalyticsEvent.PasswordSubmitted,
      signupTypeProps({ signupType: effectiveSignupType, isGoogleSignup }),
    );
    await goToNextStep();
  });

  const nextStepIsCompany = buildRouteSequence({ isStartup, isGoogleSignup }).includes('company');

  return (
    <Box data-testid="signup-step-password">
      <PanelHeader>
        <PanelTitle level="1" data-test-signup-form-heading>
          Create your password
        </PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Paragraph variant="secondary" mb="200" data-test-signup-form-subheading>
          Keep your account secure with a strong password.
        </Paragraph>

        <Form onSubmit={onSubmit}>
          <Box mb="300">
            <TextField
              {...passwordField}
              label="Password"
              type="password"
              // The blocking message lives in the meter's popover, but the
              // field still reddens while the password is unacceptable.
              isInvalid={error != null}
              autoFocus
              shouldAllowAutofill
              data-test-signup-form-input="password"
              onInput={(event) => {
                passwordField.onInput(event);
                patchFormValues({ password: event.target.value });
                // The must-match rule depends on the password, so refresh the
                // confirmation's error when it changes underneath it.
                if (form.formState.touchedFields.passwordConfirmation === true) {
                  void form.trigger('passwordConfirmation');
                }
              }}
              onBlur={() => {
                passwordField.onBlur();
                if (!isEmptyString(form.getValues('password'))) {
                  setPasswordVisited(true);
                }
              }}
            />

            {showStrengthFeedback ? (
              <>
                <Box mt="100">
                  <PasswordStrengthMeter
                    score={score}
                    isLoading={isValidating}
                    // A blocking length/breach/strength error outranks any
                    // softer zxcvbn warning in the readout.
                    warning={error ?? warning}
                    suggestions={suggestions}
                  />
                </Box>
                <Paragraph variant="secondary" sizeVariant="xs">
                  {RECOMMENDATION}
                </Paragraph>
              </>
            ) : null}
          </Box>

          <Box
            mb="200"
            className={`${styles.confirmationField} ${password !== '' ? styles.confirmationFieldVisible : ''}`}
          >
            <TextField
              {...confirmationFieldProps}
              label="Password confirmation"
              type="password"
              shouldAllowAutofill
              shouldShowPasswordVisibilityToggle={false}
              data-test-signup-form-input="password_confirmation"
              onInput={(event) => {
                confirmationFieldProps.onInput(event);
                patchFormValues({ passwordConfirmation: event.target.value });
                setConfirmationTouched(true);
              }}
            />
          </Box>

          <Flex justifyContent="flex-end" mt="200">
            <Button
              type="submit"
              variant="primary"
              icon="chevron-right"
              iconPosition="trailing"
              isDisabled={!canSubmit}
              isLoading={form.formState.isSubmitting}
              data-test-signup-form-submit
            >
              {nextStepIsCompany ? 'Next: Set up company' : 'Next: How you will use Customer.io'}
            </Button>
          </Flex>
        </Form>
      </PanelSection>
    </Box>
  );
}
